(() => {
  const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
  let categoryMotionFrame = null;
  let categoryMotionBound = false;

  const updateCategoryScrollMotion = () => {
    const viewportHeight = window.innerHeight;
    const depth = [0.7, 1, 0.82, 1.08];

    document.querySelectorAll(".labelle-category-grid").forEach((section) => {
      const rect = section.getBoundingClientRect();
      const travel = viewportHeight + rect.height;
      const progress = Math.max(0, Math.min(1, (viewportHeight - rect.top) / travel)) - 0.5;

      section.querySelectorAll(".labelle-category-card__media").forEach((media, index) => {
        const offset = progress * 12 * depth[index % depth.length];
        media.style.setProperty("--labelle-card-drift", `${offset.toFixed(2)}px`);
      });
    });

    categoryMotionFrame = null;
  };

  const requestCategoryScrollMotion = () => {
    if (categoryMotionFrame !== null) return;
    categoryMotionFrame = window.requestAnimationFrame(updateCategoryScrollMotion);
  };

  const initCategoryScrollMotion = () => {
    if (reducedMotion.matches) {
      document.querySelectorAll(".labelle-category-card__media").forEach((media) => {
        media.style.removeProperty("--labelle-card-drift");
      });
      return;
    }

    if (!categoryMotionBound) {
      window.addEventListener("scroll", requestCategoryScrollMotion, { passive: true });
      window.addEventListener("resize", requestCategoryScrollMotion);
      reducedMotion.addEventListener("change", initCategoryScrollMotion);
      categoryMotionBound = true;
    }

    requestCategoryScrollMotion();
  };

  const initTextColumnCarousels = (scope = document) => {
    if (window.innerWidth >= 750 || typeof window.Flickity === "undefined") return;

    scope.querySelectorAll(".swipe_on_mobile__text-columns").forEach((element) => {
      if (element.closest(".labelle-trust") || element.classList.contains("flickity-enabled")) return;

      new window.Flickity(element, {
        adaptiveHeight: false,
        prevNextButtons: false,
        pageDots: true,
        wrapAround: false,
      });
    });
  };

  const initProductGalleryProgress = (scope = document) => {
    scope.querySelectorAll(".product__main-photos .flickity-page-dots").forEach((dots) => {
      if (dots.dataset.labelleProgressReady === "true") return;

      const updateProgress = () => {
        const items = Array.from(dots.children);
        const total = items.length;

        if (!total) return;

        items.forEach((item, index) => {
          item.removeAttribute("aria-label");
          item.removeAttribute("role");
          item.removeAttribute("tabindex");

          let button = item.querySelector(".labelle-product-gallery-dot");
          if (!button) {
            button = document.createElement("button");
            button.type = "button";
            button.className = "labelle-product-gallery-dot";
            button.addEventListener("click", (event) => {
              event.stopPropagation();
              item.click();
            });
            item.appendChild(button);
          }

          button.setAttribute("aria-label", `View image ${index + 1} of ${total}`);
          if (item.classList.contains("is-selected") || item.getAttribute("aria-current") === "true") {
            button.setAttribute("aria-current", "true");
          } else {
            button.removeAttribute("aria-current");
          }
        });
      };

      const observer = new MutationObserver(updateProgress);
      observer.observe(dots, {
        attributes: true,
        attributeFilter: ["class", "aria-current"],
        childList: true,
        // Only observe Flickity's direct dot items. Observing the full subtree
        // also catches aria-current updates on the accessible buttons created
        // above, causing updateProgress to continuously trigger itself.
        subtree: false,
      });

      dots.dataset.labelleProgressReady = "true";
      updateProgress();
    });

    scope.querySelectorAll(".product__main-photos").forEach((gallery) => {
      if (gallery.dataset.labelleGalleryNavReady === "true") return;

      const slideshow = gallery.querySelector("[data-product-photos]");
      const previousButton = gallery.querySelector("[data-labelle-gallery-previous]");
      const nextButton = gallery.querySelector("[data-labelle-gallery-next]");
      const flickity = slideshow && window.Flickity ? window.Flickity.data(slideshow) : null;

      if (!flickity || !previousButton || !nextButton) return;

      slideshow.removeAttribute("tabindex");

      const updateButtons = () => {
        const lastIndex = Math.max(0, flickity.slides.length - 1);
        previousButton.disabled = !flickity.options.wrapAround && flickity.selectedIndex === 0;
        nextButton.disabled = !flickity.options.wrapAround && flickity.selectedIndex === lastIndex;
      };

      previousButton.addEventListener("click", () => flickity.previous());
      nextButton.addEventListener("click", () => flickity.next());
      flickity.on("select", updateButtons);

      gallery.dataset.labelleGalleryNavReady = "true";
      updateButtons();
    });
  };

  const initLabelleReveals = (scope = document) => {
    const elements = scope.querySelectorAll("[data-labelle-reveal]");

    if (reducedMotion.matches || typeof window.IntersectionObserver === "undefined") {
      elements.forEach((element) => element.classList.add("is-labelle-visible"));
      return;
    }

    document.documentElement.classList.add("labelle-motion-ready");

    const observer = new window.IntersectionObserver((entries, instance) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add("is-labelle-visible");
        instance.unobserve(entry.target);
      });
    }, {
      rootMargin: "0px 0px -6% 0px",
      threshold: 0.12,
    });

    elements.forEach((element) => {
      if (element.dataset.labelleRevealReady === "true") return;
      element.dataset.labelleRevealReady = "true";
      observer.observe(element);
    });
  };

  const initNativeDetailsKeyboard = (scope = document) => {
    scope.querySelectorAll("details > summary").forEach((summary) => {
      if (summary.dataset.labelleKeyboardReady === "true") return;

      summary.addEventListener("keydown", (event) => {
        if (event.key !== "Enter" && event.key !== " ") return;

        const details = summary.parentElement;
        if (!details || details.tagName !== "DETAILS") return;

        event.preventDefault();
        details.open = !details.open;
      });

      summary.dataset.labelleKeyboardReady = "true";
    });
  };

  const initDiscountCodes = (scope = document) => {
    const storageKey = "labelleDiscountCode";
    const components = scope.querySelectorAll("[data-labelle-discount]");

    const normalizeCode = (value) => value.trim().toLowerCase();

    const getAppliedDiscountTitles = (cart) => {
      const titles = [];

      (cart.cart_level_discount_applications || []).forEach((application) => {
        if (application && application.title) titles.push(application.title);
      });

      (cart.items || []).forEach((item) => {
        (item.line_level_discount_allocations || []).forEach((allocation) => {
          const application = allocation && allocation.discount_application;
          if (application && application.title) titles.push(application.title);
        });

        (item.discounts || []).forEach((discount) => {
          if (discount && discount.title) titles.push(discount.title);
        });
      });

      return titles;
    };

    const refreshCartMarkup = () => {
      document.dispatchEvent(new CustomEvent("cart:build"));
    };

    const readStoredCode = () => {
      try {
        return window.sessionStorage.getItem(storageKey) || "";
      } catch (error) {
        return "";
      }
    };

    const storeCode = (code) => {
      try {
        window.sessionStorage.setItem(storageKey, code);
      } catch (error) {
        // The hidden form field still carries the code when storage is unavailable.
      }
    };

    const clearStoredCode = () => {
      try {
        window.sessionStorage.removeItem(storageKey);
      } catch (error) {
        // The cart response remains the source of truth when storage is unavailable.
      }

      document.querySelectorAll("[data-labelle-discount-code-input]").forEach((input) => {
        input.value = "";
      });
    };

    const syncCode = (code) => {
      document.querySelectorAll("[data-labelle-discount-code-input]").forEach((input) => {
        input.value = code;
      });

      document.querySelectorAll("[data-labelle-discount-input]").forEach((input) => {
        if (input.value !== code) input.value = code;
      });
    };

    components.forEach((component) => {
      if (component.dataset.labelleDiscountReady === "true") return;

      const input = component.querySelector("[data-labelle-discount-input]");
      const button = component.querySelector("[data-labelle-discount-apply]");
      const status = component.querySelector("[data-labelle-discount-status]");

      if (!input || !button || !status) return;

      const setStatus = (message, state) => {
        status.textContent = message;
        status.hidden = false;
        component.dataset.state = state;
      };

      const applyCode = async () => {
        const code = input.value.trim();

        if (!code) {
          setStatus("Enter a discount code to continue.", "error");
          input.focus();
          return;
        }

        status.hidden = true;
        component.removeAttribute("data-state");
        button.disabled = true;
        button.setAttribute("aria-busy", "true");
        const defaultLabel = button.textContent;
        button.textContent = "Checking";

        try {
          const root = window.Shopify && window.Shopify.routes
            ? window.Shopify.routes.root
            : "/";
          const response = await window.fetch(`${root}cart/update.js`, {
            method: "POST",
            credentials: "same-origin",
            headers: {
              "Accept": "application/json",
              "Content-Type": "application/json",
            },
            body: JSON.stringify({ discount: code }),
          });

          const cart = await response.json();
          if (!response.ok) {
            throw new Error(cart.description || "The discount could not be checked.");
          }

          refreshCartMarkup();

          const discountCodeResult = (cart.discount_codes || []).find((discountCode) => (
            discountCode && normalizeCode(discountCode.code || "") === normalizeCode(code)
          ));
          const appliedTitles = getAppliedDiscountTitles(cart);
          const codeWasApplied = discountCodeResult
            ? discountCodeResult.applicable === true
            : appliedTitles.some((title) => normalizeCode(title) === normalizeCode(code));

          if (!codeWasApplied) {
            clearStoredCode();
            setStatus("This code does not apply to the items in your bag.", "error");
            return;
          }

          syncCode(code);
          storeCode(code);
          setStatus(`${code.toUpperCase()} applied. Your subtotal has been updated.`, "success");
        } catch (error) {
          setStatus("We couldn't apply this code. Please try again.", "error");
        } finally {
          button.disabled = false;
          button.removeAttribute("aria-busy");
          button.textContent = defaultLabel;
        }
      };

      const storedCode = readStoredCode();
      if (storedCode) syncCode(storedCode);

      button.addEventListener("click", applyCode);
      input.addEventListener("keydown", (event) => {
        if (event.key !== "Enter") return;
        event.preventDefault();
        applyCode();
      });

      component.dataset.labelleDiscountReady = "true";
    });
  };

  const initLabelleTheme = (scope = document) => {
    initTextColumnCarousels(scope);
    initProductGalleryProgress(scope);
    initLabelleReveals(scope);
    initNativeDetailsKeyboard(scope);
    initCategoryScrollMotion();
    initDiscountCodes(scope);
  };

  const prepareImageControls = (scope = document) => {
    const buttons = [...scope.querySelectorAll('[data-labelle-image-toggle]')];
    if (scope.matches?.('[data-labelle-image-toggle]')) buttons.unshift(scope);
    buttons.forEach((button) => {
      const image = button.closest('.grid-product__content')?.querySelector('[data-labelle-secondary-image] img');
      if (!image || button.dataset.labelleImageReady) return;
      button.dataset.labelleImageReady = 'true';
      const enable = () => { if (image.naturalWidth > 0) button.hidden = false; };
      if (image.complete) enable();
      else image.addEventListener('load', enable, { once: true });
      image.addEventListener('error', () => {
        button.hidden = true;
        button.closest('.grid-product__content').classList.add('labelle-secondary-unavailable');
      }, { once: true });
    });
  };

  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-labelle-image-toggle]');
    if (!button) return;
    const card = button.closest('.grid-product__content');
    const selected = button.getAttribute('aria-pressed') !== 'true';
    card.classList.add('labelle-secondary-manual');
    card.classList.toggle('labelle-secondary-selected', selected);
    button.setAttribute('aria-pressed', String(selected));
  });

  const resetImageControl = (event) => {
    const swatch = event.target.closest('.color-swatch');
    if (!swatch) return;
    const card = swatch.closest('.grid-product');
    const button = card?.querySelector('[data-labelle-image-toggle]');
    if (!button) return;
    card.querySelector('.grid-product__content').classList.remove('labelle-secondary-selected', 'labelle-secondary-manual');
    button.setAttribute('aria-pressed', 'false');
  };
  document.addEventListener('pointerover', resetImageControl);
  document.addEventListener('focusin', resetImageControl);
  document.addEventListener('focusout', (event) => {
    const button = event.target.closest('[data-labelle-image-toggle]');
    if (!button) return;
    button.closest('.grid-product__content').classList.remove('labelle-secondary-selected', 'labelle-secondary-manual');
    button.setAttribute('aria-pressed', 'false');
  });
  document.addEventListener('DOMContentLoaded', () => {
    prepareImageControls();
    const observer = new MutationObserver((records) => {
      records.forEach((record) => record.addedNodes.forEach((node) => {
        if (node.nodeType === 1) prepareImageControls(node);
      }));
    });
    observer.observe(document.body, { childList: true, subtree: true });
  });

  document.addEventListener("DOMContentLoaded", () => initLabelleTheme());
  document.addEventListener("shopify:section:load", (event) => initLabelleTheme(event.target));
})();
